import {
  flexRender, getCoreRowModel, useReactTable,
  type ColumnDef, type OnChangeFn, type RowSelectionState,
} from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  onRowClick?: (row: T) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: T, idx: number) => string;
  /** Optional empty-state node when there are no rows. */
  empty?: React.ReactNode;
}

export function DataTable<T>({
  columns, data, onRowClick,
  rowSelection, onRowSelectionChange, getRowId,
  empty,
}: DataTableProps<T>) {
  const table = useReactTable({
    data, columns,
    getCoreRowModel: getCoreRowModel(),
    state: rowSelection ? { rowSelection } : undefined,
    onRowSelectionChange,
    getRowId,
    enableRowSelection: !!rowSelection,
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="rounded-lg border border-zinc-800/80 bg-bg-2 overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="hover:bg-transparent">
              {hg.headers.map((h) => (
                <TableHead key={h.id}>
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="h-32 text-center text-sm text-zinc-500"
              >
                {empty ?? 'Ничего не найдено'}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                onClick={(e) => {
                  const tag = (e.target as HTMLElement).tagName.toLowerCase();
                  if (tag === 'input' || tag === 'button') return;
                  onRowClick?.(row.original);
                }}
                className={cn(onRowClick && 'cursor-pointer')}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
